import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Page, PartnerType } from '@clawhire/shared';
import { api, type QueryParams } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import type {
  CandidateListItem,
  DirectoryUser,
  OptionLists,
  PartnerRef,
  PositionDetail,
  PositionListItem,
} from './types';

/** Prefix shared by every positions query (`['positions', 'list' | 'detail', …]`). */
export const POSITIONS_KEY = ['positions'] as const;

export function usePositionsList(query: QueryParams) {
  return useQuery({
    queryKey: queryKeys.list('positions', query),
    queryFn: () => api.get<Page<PositionListItem>>('/positions', { query }),
    placeholderData: (previous) => previous,
  });
}

/** `GET /positions/:ref` — the ref may be a UUID, a request number or a DC-J reference. */
export function usePosition(ref: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.detail('positions', ref ?? ''),
    queryFn: () => api.get<PositionDetail>(`/positions/${encodeURIComponent(ref ?? '')}`),
    enabled: enabled && Boolean(ref),
  });
}

/** Invalidate every positions list and detail (mutations touch computed fields on both). */
export function useInvalidatePositions() {
  const queryClient = useQueryClient();
  return React.useCallback(() => queryClient.invalidateQueries({ queryKey: POSITIONS_KEY }), [queryClient]);
}

export function usePartners(type: PartnerType) {
  return useQuery({
    queryKey: queryKeys.list('partners', { type, pageSize: 200 }),
    queryFn: () => api.get<Page<PartnerRef>>('/partners', { query: { type, pageSize: 200 } }),
    select: (page) => page.items,
    staleTime: 5 * 60_000,
  });
}

/** `GET /users/directory`: every team member, alphabetical by name (owner pickers and filters). */
export function useUsersDirectory() {
  return useQuery({
    queryKey: queryKeys.list('users', { scope: 'directory' }),
    queryFn: () => api.get<DirectoryUser[]>('/users/directory'),
    staleTime: 5 * 60_000,
  });
}

/** Options for an owner picker: active users by name, plus the current owner when inactive. */
export function ownerOptions(
  users: readonly DirectoryUser[] | undefined,
  current?: { id: string; name: string } | null,
): { value: string; label: string }[] {
  const list = (users ?? []).filter((u) => u.active).map((u) => ({ value: u.id, label: u.name }));
  if (current && !list.some((o) => o.value === current.id))
    list.unshift({ value: current.id, label: current.name });
  return list;
}

export function useOptions() {
  return useQuery({
    queryKey: queryKeys.options,
    queryFn: () => api.get<OptionLists>('/options'),
    staleTime: 5 * 60_000,
  });
}

export function useCandidateSearch(text: string, enabled = true) {
  const q: QueryParams = { text: text.trim() || undefined, pageSize: 20 };
  return useQuery({
    queryKey: queryKeys.list('candidates', { ...q, scope: 'propose' }),
    queryFn: () => api.get<Page<CandidateListItem>>('/candidates', { query: q }),
    select: (page) => page.items,
    enabled,
    placeholderData: (previous) => previous,
  });
}
